/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Skeleton, Typography } from '@mui/material'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useEffect, useState } from 'react'
import { useRefresh } from '../../ContextProviders/RefreshProvider'
import { sendGet } from '../../hooks/use-fetch'
import { ToolTip } from '../CustomLabel/ToolTip'
import '../../core/styles.css'

function StatusDisplay (props: { checked: boolean, date?: Date, warning?: boolean, warningText?: string, text: string }): JSX.Element {
  return <Box data-testid={`statusDisplay${props.text}`} sx={{ display: 'flex', flexDirection: 'row', gap: 1, py: '5px' }}>
    {props.checked
      ? <CheckCircleIcon color='primary' data-testid={`${props.text}checked`} sx={{ width: '30px', height: '30px' }} />
      : <RadioButtonUncheckedIcon data-testid={`${props.text}unchecked`} sx={{ width: '30px', height: '30px' }}
          color='primary'
      />}
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>

      <Typography variant={'body1'} sx={{ alignContent: 'center' }}>{props.text}</Typography>
      {props.date != null && (
        <Typography variant='body2' sx={{ alignContent: 'center' }}>{new Date(props.date).toDateString()}</Typography>
      )}
    </Box>
    {(props.warning ?? false) && <ToolTip variant='warning' text={props.warningText ?? ''} />}
  </Box>
}
export function ActionTracker (props: { enrollmentId: number }): JSX.Element {
  const [events, setEvents] = useState<Array<{ id: number, name: string, date?: Date }> | null>(null)
  const [warnings, setWarnings] = useState<{ ageWarning: boolean, incomeWarning: boolean, programWarning: boolean }>({ ageWarning: false, incomeWarning: false, programWarning: false })
  const [loaded, setLoaded] = useState(false)
  const refresh = useRefresh()
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/GetEnrollmentActions?id=${props.enrollmentId}`, {})
      setEvents(response.enrollmentActions)
      setWarnings({ ageWarning: response.ageInapropriate, incomeWarning: response.incomeUnverified, programWarning: response.programInvalid })
      setLoaded(true)
    }
    void loadData()
  }, [refresh, props.enrollmentId])
  if (!loaded) { return <Skeleton variant='rectangular' width={'300px'} height='350px' /> }
  if (events == null) return <></>
  const pendingItem = events.find(e => e.name === 'PendingRelease')
  const referredItem = events.find(e => e.name === 'Referred')
  const declinedItem = events.find(e => e.name === 'Declined')
  const acceptedItem = events.find(e => e.name === 'Processing')
  const waitListedItem = events.find(e => e.name === 'WaitListed')
  const enrolledItem = events.find(e => e.name === 'Enrolled')
  const startedItem = events.find(e => e.name === 'Started')
  const exitedItem = events.find(e => e.name === 'Exited')

  return <Box data-testid='ActionTracker'
    className='CardDisplay'
  >
    <Typography variant='h6' sx={{ fontWeight: 'bold' }}>Action Tracker</Typography>
    <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Prepare</Typography>
    <StatusDisplay checked={pendingItem != null} date={pendingItem?.date} text={'Pending Release'}
      warning={warnings.programWarning} warningText='This enrollment needs a program to continue processing'
    />
    <StatusDisplay checked={referredItem != null} date={referredItem?.date}
      warning={warnings.ageWarning} warningText='This child does not meet the age requirements for this program.' text={'Referred'}
    />
    {(declinedItem != null) && (
      <StatusDisplay checked={declinedItem != null} date={declinedItem?.date} text={'Declined'} />
    )}
    {(declinedItem == null) && (<>
      <StatusDisplay checked={acceptedItem != null} date={acceptedItem?.date} text={'Processing'} />
      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Enroll</Typography>
      {waitListedItem != null && <StatusDisplay checked={waitListedItem != null} date={waitListedItem?.date} text={'Wait Listed'} />}
      <StatusDisplay checked={enrolledItem != null} date={enrolledItem?.date} warning={acceptedItem != null && warnings.incomeWarning} warningText={'The income verification for this program is not present for the requested school year.'} text={'Enrolled'} />
      <StatusDisplay checked={startedItem != null} date={startedItem?.date} text={'Started'} />
      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Exit</Typography>
      <StatusDisplay checked={exitedItem != null} date={exitedItem?.date} text={'Exited'} />
    </>
    )}
  </Box>
}
